import { postUrl } from '@/apiCalls/fetch';
import { setBorrower } from '@/context/borrower';
import {
    EDisplays,
    EModals,
    setAgreedToTermsAndConditions,
    // setDisplay,
    setModal,
} from '@/context/display';
import { store } from '@/context/store';
import {
    ELocalStorageVariables,
    getLocalStorageItem,
    setLocalStorageItem,
} from '@/utils/localStorage';

import { IGetBorrowerReturn } from './getBorrowerTypes';

export async function getBorrower(): Promise<void> {
    // ***** Redux *****
    const state = store.getState();
    const display = state.displayReducer.display;
    const token = getLocalStorageItem(ELocalStorageVariables.AUTH_TOKEN);
    const vehicle = state.borrowerReducer.vehicle;
    const seller = state.borrowerReducer.seller;
    const loan = state.borrowerReducer.loan;
    const isAssetKnownViaParams = state.displayReducer.isAssetKnownViaParams;
    const currentLeadPartnerId =
        state.borrowerReducer.application.partnerOverwriteId;

    const partnerId = state.displayReducer.partner.id;
    const currentAssetType = state.borrowerReducer.vehicle.assetType;
    const currentAssetCategory = state.borrowerReducer.vehicle.assetCategory;
    const isAssetTypeKnownViaParams =
        state.borrowerReducer.vehicle.isAssetTypeKnownViaParams;
    const currentStockNumber = state.borrowerReducer.vehicle.stockNumber;
    const testDrives = state.borrowerReducer.testDrives;
    const isVehicleKnown = state.borrowerReducer.vehicle.isVehicleKnown;
    if (!token) {
        console.log('No token found in local storage');
        return;
    }

    // ***** Request *****
    const url = '/api/borrower/get';

    const body = {
        partnerId: state.displayReducer.partner.id,
    };
    console.log('borrower', state.borrowerReducer);

    const response = await postUrl<IGetBorrowerReturn>(url, body);

    // ***** Update Redux *****
    const returnedBorrower = { ...response.borrower };

    // BE does not save these values
    // BE does not save these values

    const feAddress = state.borrowerReducer.address;
    const backendAddress = returnedBorrower.address?.address;
    const backendAddressHasData =
        backendAddress &&
        Array.isArray(backendAddress) &&
        backendAddress.length > 0;
    const feAddressHasData =
        feAddress?.address &&
        Array.isArray(feAddress.address) &&
        feAddress.address.length > 0;

    // Prioritize backend address if it exists and has data, otherwise use frontend address
    // This ensures returning customers get their saved address loaded
    if (!returnedBorrower.address) {
        returnedBorrower.address = {
            addressIndex: 0,
            address: [],
        };
    }

    returnedBorrower.address = {
        addressIndex:
            returnedBorrower.address.addressIndex ??
            feAddress?.addressIndex ??
            0,
        address: backendAddressHasData
            ? backendAddress
            : feAddressHasData
            ? feAddress.address
            : returnedBorrower.address.address ?? [],
    };
    if (!returnedBorrower.employments) {
        returnedBorrower.employments = {
            currentEmploymentIndex: 0,
            employments: [],
        };
    }
    if (
        !returnedBorrower.personalLoans ||
        !returnedBorrower.personalLoans.personalLoans
    ) {
        returnedBorrower.personalLoans = {
            personalLoanActiveIndex: 0,
            personalLoans: [],
        };
    }
    if (
        !returnedBorrower.creditCards ||
        !returnedBorrower.creditCards.creditCards
    ) {
        returnedBorrower.creditCards = {
            creditCardActiveIndex: 0,
            creditCards: [],
        };
    }
    if (!returnedBorrower.motorVehicles) {
        returnedBorrower.motorVehicles = {
            motorVehicleActiveIndex: 0,
            motorVehicles: [],
        };
    }
    if (!returnedBorrower.realEstates) {
        returnedBorrower.realEstates = {
            realEstateActiveIndex: 0,
            realEstates: [],
        };
    }

    returnedBorrower.address.addressIndex = 0;
    returnedBorrower.employments.currentEmploymentIndex = 0;
    returnedBorrower.personalLoans.personalLoanActiveIndex = 0;
    returnedBorrower.creditCards.creditCardActiveIndex = 0;
    returnedBorrower.motorVehicles.motorVehicleActiveIndex = 0;
    returnedBorrower.realEstates.realEstateActiveIndex = 0;

    // BE Would otherwise overwrite FE vehicle / seller set by FE params which is incorrect
    if (isAssetKnownViaParams || isVehicleKnown) {
        // Only use new vehicle if it's present
        returnedBorrower.seller = { ...seller };
        returnedBorrower.vehicle = { ...vehicle };
    }

    // Do not overwrite the vehicle asset type if it is known via params
    if (isAssetTypeKnownViaParams || !returnedBorrower.vehicle?.assetType) {
        returnedBorrower.vehicle.assetType = currentAssetType;
        returnedBorrower.vehicle.assetCategory = currentAssetCategory;
    }

    // only update loan if it is not coming from the parameters
    returnedBorrower.loan.deposit =
        loan.deposit || returnedBorrower.loan.deposit;
    returnedBorrower.loan.loanTerm =
        loan.loanTerm || returnedBorrower.loan.loanTerm;
    returnedBorrower.loan.balloon =
        loan.balloon || returnedBorrower.loan.balloon;
    returnedBorrower.loan.tradeIn =
        loan.tradeIn || returnedBorrower.loan.tradeIn;
    // returnedBorrower.loan.businessFinanceType =
    //     loan.businessFinanceType || returnedBorrower.loan.businessFinanceType;

    // Partner Id overwrite to always be set to the current value
    returnedBorrower.application.partnerOverwriteId = currentLeadPartnerId;
    // Always use the value passed in the params, or undefined otherwise
    returnedBorrower.vehicle.stockNumber = currentStockNumber;

    returnedBorrower.vehicle.annualKms = response.borrower.vehicle.annualKms;
    returnedBorrower.loan.purchasePrice =
        loan.purchasePrice ?? response.borrower.loan.purchasePrice;

    // only update loanUsage if BE gave one, otherwise keep FE value
    returnedBorrower.loan.loanUsage =
        response.borrower.loan.loanUsage ?? loan.loanUsage;

    // Always preserve frontend test drive data to ensure current time is shown
    // This prevents flashing back to previous application times
    returnedBorrower.testDrives = testDrives;

    if (
        !returnedBorrower.income ||
        Object.keys(returnedBorrower.income).length === 0
    ) {
        returnedBorrower.income = state.borrowerReducer.income;
    }

    if (
        !returnedBorrower.employments.employments ||
        Object.keys(returnedBorrower.employments.employments).length === 0
    ) {
        returnedBorrower.employments = state.borrowerReducer.employments;
    }
    if (
        !returnedBorrower.expenses ||
        Object.keys(returnedBorrower.expenses).length === 0
    ) {
        returnedBorrower.expenses = state.borrowerReducer.expenses;
    }
    if (!returnedBorrower.personalDetails.hasChildren) {
        returnedBorrower.personalDetails.hasChildren =
            state.borrowerReducer.personalDetails.hasChildren;
    }
    if (
        state.borrowerReducer.vehicle.annualKms &&
        !returnedBorrower.vehicle.annualKms
    ) {
        returnedBorrower.vehicle.annualKms =
            state.borrowerReducer.vehicle.annualKms;
    }

    // Preserve frontend selfAssessedCreditHistory as it's not saved on backend
    if (state.borrowerReducer.scores.selfAssessedCreditHistory) {
        returnedBorrower.scores = {
            ...returnedBorrower.scores,
            selfAssessedCreditHistory:
                state.borrowerReducer.scores.selfAssessedCreditHistory,
        };
    }

    store.dispatch(setBorrower(returnedBorrower));
    const returnedFirstName = response.borrower.personalDetails.firstName;
    if (returnedFirstName) {
        setLocalStorageItem(
            ELocalStorageVariables.FIRST_NAME,
            returnedFirstName
        );
    }

    store.dispatch(
        setAgreedToTermsAndConditions(
            returnedBorrower.personalDetails.termsAndConditions
        )
    );

    // ***** Update Display *****
    if (display === EDisplays.SERVICING_SUCCESS) return;

    if (returnedBorrower.application.isSubmitted) {
        store.dispatch(setModal(EModals.APPLICATION_IN_PROGRESS));
        return;
    }
}
